import { useRef } from 'react';

import styled from 'styled-components';
import { isEmpty } from 'lodash';

import Table, { Column } from '@paradigm/design-system/src/components/Table';
import { BaseLeaderboardEntity } from '@paradigm/features/src/leaderboard/entities/shared';
import LoadingSpinner from '@paradigm/design-system/src/components/LoadingSpinner';
import EmptyTablePage from './EmptyTablePage';
import buildStyles from './useStyles';
import useSettings from './useSettings';

export type LeaderboardColumn<T> = Column<T> & {
  readonly sortingDirection?: 'asc' | 'desc';
  readonly isDefaultSorting?: true;
};
interface Props<T extends BaseLeaderboardEntity> {
  readonly status: 'idle' | 'error' | 'loading';
  readonly columns: LeaderboardColumn<T>[];
  readonly data: T[];
  readonly tableRowAlign?: 'default' | 'right';
}

function LeaderboardTable<T extends BaseLeaderboardEntity>({
  status,
  columns,
  data,
  tableRowAlign = 'default',
  ...rest
}: Props<T>) {
  const tableRef = useRef<HTMLDivElement>(null);

  const tableStyles = buildStyles(tableRowAlign);
  const { settings } = useSettings<T>(columns);

  if (status === 'loading')
    return (
      <div css="text-align: center; padding: 16px;">
        <LoadingSpinner size={40} alt="loading spinner" />
      </div>
    );

  if (isEmpty(data))
    return (
      <div css="text-align: center;">
        <EmptyTablePage />
      </div>
    );

  return (
    <Container>
      <Table<T>
        styles={tableStyles}
        columns={columns}
        data={data}
        tableContainerRef={tableRef}
        settings={settings}
        enableSortingRemoval={false}
        getRowTestId={(entity, index) =>
          entity.isFromUser
            ? 'leaderboard-row-user'
            : `leaderboard-row-${index}`
        }
        {...rest}
      />
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

export default LeaderboardTable;
